import React, { useEffect, useState } from "react";
import axios from "axios";
import { Save, Plus, Trash2, Eye } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("admin_token")}` });

export default function AdminContent() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/content`).then((r) => { setContent(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const update = (key, val) => setContent((prev) => ({ ...prev, [key]: val }));
  const updateStat = (i, key, val) => {
    const stats = [...content.stats];
    stats[i] = { ...stats[i], [key]: val };
    setContent((prev) => ({ ...prev, stats }));
  };
  const addStat = () => setContent((prev) => ({ ...prev, stats: [...(prev.stats || []), { value: "", label: "" }] }));
  const removeStat = (i) => setContent((prev) => ({ ...prev, stats: prev.stats.filter((_, idx) => idx !== i) }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/content`, content, { headers: getHeaders() });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch { alert("Failed to save content."); }
    setSaving(false);
  };

  if (loading || !content) return <div className="flex items-center justify-center h-64 text-gray-400">Loading content...</div>;

  const fieldClass = "w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-900 bg-white";
  const Label = ({ text }) => <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wide">{text}</label>;

  return (
    <div data-testid="admin-content-page" className="space-y-6 pb-10">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-extrabold" style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}>Website Content</h2>
          <p className="text-sm text-gray-400 mt-0.5">Edit hero, stats, and about section content</p>
        </div>
        <div className="flex gap-3">
          <a href="/" target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border border-gray-200 hover:bg-gray-50">
            <Eye size={15} /> Preview Site
          </a>
          <button onClick={handleSave} disabled={saving} data-testid="content-save-btn"
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60"
            style={{ backgroundColor: saved ? "#10b981" : "#0A2540" }}>
            <Save size={15} /> {saving ? "Saving..." : saved ? "Saved!" : "Save Changes"}
          </button>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
        <h3 className="font-bold text-base mb-5 pb-3 border-b border-gray-100" style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}>
          Hero Section
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label text="Main Title (white text)" />
            <input type="text" value={content.hero_title} onChange={(e) => update("hero_title", e.target.value)} placeholder="Where Dreams" className={fieldClass} data-testid="content-hero-title" />
          </div>
          <div>
            <Label text="Gold Title (gold text)" />
            <input type="text" value={content.hero_title_gold} onChange={(e) => update("hero_title_gold", e.target.value)} placeholder="Meet Reality" className={fieldClass} data-testid="content-hero-title-gold" />
          </div>
          <div className="md:col-span-2">
            <Label text="Subtitle / Description" />
            <textarea value={content.hero_subtitle} onChange={(e) => update("hero_subtitle", e.target.value)} rows={3} className={`${fieldClass} resize-none`} data-testid="content-hero-subtitle" />
          </div>
          <div className="md:col-span-2">
            <Label text="Hero Image URL" />
            <input type="url" value={content.hero_image} onChange={(e) => update("hero_image", e.target.value)} placeholder="https://..." className={fieldClass} data-testid="content-hero-image" />
            {content.hero_image && (
              <img src={content.hero_image} alt="Hero preview" className="mt-2 h-28 w-full object-cover rounded-lg border" onError={(e) => { e.target.style.display = "none"; }} />
            )}
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
        <div className="flex items-center justify-between mb-5 pb-3 border-b border-gray-100">
          <h3 className="font-bold text-base" style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}>Stats / Numbers</h3>
          <button onClick={addStat} className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50">
            <Plus size={13} /> Add Stat
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(content.stats || []).map((stat, i) => (
            <div key={i} className="flex items-center gap-2 p-3 rounded-xl bg-gray-50 border border-gray-100">
              <div className="flex-1 grid grid-cols-2 gap-2">
                <input type="text" value={stat.value} onChange={(e) => updateStat(i, "value", e.target.value)}
                  placeholder="e.g. 1000+" className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-900 bg-white font-bold" />
                <input type="text" value={stat.label} onChange={(e) => updateStat(i, "label", e.target.value)}
                  placeholder="Label" className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-900 bg-white" />
              </div>
              <button onClick={() => removeStat(i)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 flex-shrink-0">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* About Section */}
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
        <h3 className="font-bold text-base mb-5 pb-3 border-b border-gray-100" style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}>
          About Page Content
        </h3>
        <div className="space-y-4">
          <div>
            <Label text="Academy Story" />
            <textarea value={content.about_story} onChange={(e) => update("about_story", e.target.value)}
              rows={4} placeholder="The founding story of Trident Academy..." className={`${fieldClass} resize-none`} data-testid="content-about-story" />
          </div>
          <div>
            <Label text="Mission Statement" />
            <textarea value={content.about_mission} onChange={(e) => update("about_mission", e.target.value)}
              rows={3} className={`${fieldClass} resize-none`} data-testid="content-about-mission" />
          </div>
          <div>
            <Label text="Vision Statement" />
            <textarea value={content.about_vision} onChange={(e) => update("about_vision", e.target.value)}
              rows={3} className={`${fieldClass} resize-none`} data-testid="content-about-vision" />
          </div>
        </div>
      </div>

      {/* Save again at bottom */}
      <div className="flex justify-end">
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-2 px-8 py-3 rounded-xl text-white text-sm font-bold hover:opacity-90 disabled:opacity-60"
          style={{ backgroundColor: saved ? "#10b981" : "#0A2540" }}>
          <Save size={15} />{saving ? "Saving..." : saved ? "Saved!" : "Save All Changes"}
        </button>
      </div>
    </div>
  );
}
